import { useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'

/**
 * The club's course book, and one course's card.
 *
 * `GET /golf/courses` with NO coordinates is the BOOK — every course in
 * `Fireside::Golf::Courses`, alphabetically, with the scorecard the club
 * actually holds for each and nothing about tee times. It is deliberately not
 * the nearest-ten search with the filters left off: no distance (there is no
 * origin to measure from), no cap, no tee sheet.
 *
 * Both endpoints are PUBLIC — a scorecard is not a secret, and a visitor
 * deciding whether to apply should be able to see where the club plays. So
 * neither hook takes an `enabled` flag and neither is wrapped in `BookGate`:
 * there is no gate to render.
 *
 * The card is fetched ON DEMAND, one course at a time. Fifteen scorecards
 * eagerly is fifteen requests for the one a member is going to read.
 */

/** One course as the book prints it (CoursesController#book_entry). */
export interface CourseBookEntry {
  key: string
  name: string
  operator: string
  city: string
  holes_count: number
  /** ALL OR NOTHING — null unless every hole of the course is carded. */
  par: number | null
  /** ALL OR NOTHING — null unless every carded hole is also measured. */
  total_yardage: number | null
  tee_count: number
  /** Somebody has traced this course AND published it. Not "has a draft". */
  mapped: boolean
}

/** One hole of a course's card (#course_hole_json). */
export interface CourseHole {
  number: number
  par: number
  stroke_index: number
  yardage: number | null
}

/** One tee box of a course's card (#tee_box_json). */
export interface CourseTeeBox {
  id: string
  name: string
  color: string | null
  course_rating: number | null
  slope: number | null
  total_yardage: number | null
  rated: boolean
}

/** A course's full card (#golf_scorecard_json). */
export interface CourseCardPayload {
  id: string
  key: string
  name: string
  city: string
  holes_count: number
  par: number | null
  tee_boxes: CourseTeeBox[]
  holes: CourseHole[]
}

interface CourseBookResponse {
  courses: CourseBookEntry[]
}
interface CourseCardResponse {
  course: CourseCardPayload
}

export function useCourseBook() {
  const [courses, setCourses] = useState<CourseBookEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    api
      .get<CourseBookResponse>('/golf/courses')
      .then((book) => {
        if (live) setCourses(book.courses)
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : 'Could not open the course book.')
      })
      .finally(() => {
        if (live) setIsLoading(false)
      })
    return () => {
      live = false
    }
  }, [])

  return { courses, isLoading, error }
}

/**
 * One course's card, fetched when a course is chosen and not before.
 *
 * `notCarded` is the honest half of a 404 here and is NOT an error: the
 * registry is the list of courses and a scorecard is a separate fact that may
 * not have been seeded yet (the endpoint 404s rather than serving an empty
 * card, precisely so the two can be told apart). A member choosing an unseeded
 * course should read "the club has not carded this one yet", not "something
 * went wrong".
 */
/** What one course's card came back as — stamped with the key it answers. */
interface CardResult {
  key: string
  card: CourseCardPayload | null
  notCarded: boolean
  error: string | null
}

export function useCourseCard(key: string | null) {
  const [result, setResult] = useState<CardResult | null>(null)

  useEffect(() => {
    if (key === null) return
    let alive = true
    void api
      .get<CourseCardResponse>(`/golf/courses/${key}`)
      .then((payload) => {
        if (alive) setResult({ key, card: payload.course, notCarded: false, error: null })
      })
      .catch((e: unknown) => {
        if (!alive) return
        // A 404 is the club saying "we have not carded this one", which the
        // endpoint answers deliberately rather than serving an empty card.
        const notCarded = e instanceof ApiError && e.status === 404
        setResult({
          key,
          card: null,
          notCarded,
          error: notCarded
            ? null
            : e instanceof Error
              ? e.message
              : 'Could not read that course’s card.',
        })
      })
    return () => {
      alive = false
    }
  }, [key])

  // Derived, never stored: an answer counts only for the course it was asked
  // about, so switching courses reads as loading again rather than showing the
  // previous card under the new name. This is also why the effect above sets
  // no state synchronously — a "clear it first" write is a cascading render.
  const settled = result !== null && result.key === key ? result : null

  return {
    card: settled?.card ?? null,
    notCarded: settled?.notCarded ?? false,
    error: settled?.error ?? null,
    isLoading: key !== null && settled === null,
  }
}
